import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { FormSelect, useFormContext } from "@jsq/ds/forms";
import { type ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import { useChangeAndResetDocumentData } from "../../hooks/useChangeAndResetDocumentData";
import { ResetDocumentDataWarningModal } from "../../ResetDocumentDataWarningModal";
import { useResetWarningModal } from "../../ResetDocumentDataWarningModal/useResetWarningModal";
import { FormValues } from "../../types";
import { isBaseDocumentType, isTaxDocumentType } from "../../utils";
import { subTypeDocumentSelector } from "./styles_vanilla.css";
import { SubTypeDocumentSelectorOption } from "./types";

export type Props = {
  readonly: boolean;
  subTypeDocuments: Array<SubTypeDocumentSelectorOption>;
  name: string;
  label: string;
  placeholder?: string;
  className?: string;
  documentName?: string;
};

const SubTypeDocumentSelector = ({
  readonly,
  subTypeDocuments,
  placeholder,
  name,
  label,
  documentName = "",
}: Props) => {
  const { values, setFieldValue } = useFormContext<FormValues>();
  const { caseId, caseType, fatcaCrsEnabled } = useCurrentCaseContext();
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );
  const location = useLocation();
  const currentValue = values[name as keyof FormValues] as string;

  const searchParams = new URLSearchParams(location.search);
  const documentIdStr = searchParams.get("documentId");
  const documentId = documentIdStr ? parseInt(documentIdStr, 10) : 0;

  const { changeAndReset } = useChangeAndResetDocumentData({
    caseId,
    documentId,
  });

  const shouldShowModal = useCallback(() => {
    return isTaxDocumentType(values.documentType);
  }, [values.documentType]);

  const getSubTypeReadableName = useCallback(
    (typeValue: string) => {
      const option = subTypeDocuments.find((opt) => opt.value === typeValue);
      return option?.readableName ?? typeValue;
    },
    [subTypeDocuments],
  );

  const handleSubTypeChangeConfirm = useCallback(
    async (newValue: string) => {
      return changeAndReset({
        resetInfo: {
          profileId: Number(values.relatedProfile),
          documentPurpose: isBaseDocumentType(values.documentType) ? values.documentType : null,
          documentStatus: null,
          subType: newValue,
        },
      });
    },
    [changeAndReset, values.relatedProfile, values.documentType],
  );

  const handleSubTypeDirectChange = useCallback(
    (newValue: string) => {
      void setFieldValue(name, newValue);
    },
    [setFieldValue, name],
  );

  const handleSubTypeChangeCancel = useCallback(
    (previousValue: string) => {
      void setFieldValue(name, previousValue);
    },
    [setFieldValue, name],
  );

  const {
    showModal: showResetModal,
    pendingChange,
    isLoading: isModalLoading,
    handleFieldChange: handleSubTypeFieldChange,
    handleConfirm: handleResetConfirm,
    handleCancel: handleResetCancel,
  } = useResetWarningModal({
    onConfirm: handleSubTypeChangeConfirm,
    onCancel: handleSubTypeChangeCancel,
    onDirectChange: handleSubTypeDirectChange,
    getDocumentName: () => documentName,
    getCurrentValue: () => currentValue ?? "",
    getReadableValue: getSubTypeReadableName,
    changeType: "subType",
    shouldShowModal,
  });

  const handleSubTypeSelectChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const newValue = String(value);
      if (isFatcaCrsEnabled && currentValue) {
        handleSubTypeFieldChange(newValue);
      } else {
        void setFieldValue(name, newValue);
      }
      // Reset date and expiryDate when sub-type changes; once FATCA/CRS is enabled these will be handled by resetDocumentFormFields
      if (!isFatcaCrsEnabled && newValue !== currentValue) {
        void setFieldValue("date", undefined);
        void setFieldValue("expiryDate", undefined);
      }
    },
    [isFatcaCrsEnabled, currentValue, handleSubTypeFieldChange, setFieldValue, name],
  );

  return (
    <>
      <FormSelect
        className={subTypeDocumentSelector}
        disabled={readonly}
        placeholder={placeholder || "Select a document type"}
        name={name}
        label={label}
        full
        handleChange={handleSubTypeSelectChange}
      >
        <FormSelect.Section>
          {subTypeDocuments.map((type) => (
            <FormSelect.Option key={type.value} value={type.value}>
              {type.readableName}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
      <ResetDocumentDataWarningModal
        show={showResetModal}
        onHide={handleResetCancel}
        onConfirm={handleResetConfirm}
        changeDetails={pendingChange}
        isLoading={isModalLoading}
      />
    </>
  );
};

export default SubTypeDocumentSelector;
